import {RelayPositions, RelayPositionValues, relayPlayerKey, missingRelayPositions} from '../HighLevelTournamentEvents/RelayPositions';
import '../HighLevelTournamentEvents/StudentsToAddToEventForm.scss';
import '../../../AdminView/Tournaments/TournamentsHub/TournamentsHub.css';
import { StudentBirthday } from 'Src/components/StudentBirthday/StudentBirthday';
import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { IndividualTournamentTeamPlayer } from 'Src/models/tournamentTeamPlayer';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { searchFunctionHighLevelTournamentEvents } from 'Src/helpers/autocomplete/autocomplete';
import { Switch } from 'Src/components/Switch/Switch';
import { SchoolEvent } from 'Src/models/event';
import { AGE_GROUPS } from 'Src/consts/school';
import { sortTournamentEventsByAge } from 'Src/helpers/event/event';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { SlotAssignmentModal, SlotAssignment } from 'Src/views/GenericView/AdminView/Tournaments/SlotAssignmentModal';

interface PlayerToReplace {
  userId: string;
  permissionId: string;
  playerId: string;
  firstName: string;
  lastName: string;
  replacedFirstName: string;
  replacedLastName: string;
}

interface SelectedEventItem {
  event: SchoolEvent;
  playerToReplace?: PlayerToReplace;
  student?: IndividualTournamentTeamPlayer;
}

interface Props {
  isSaving?: boolean;
  user: AppUser;
  students: IndividualTournamentTeamPlayer[];
  tournamentId: string;
  addPlayerToEvent: (
    assignments: {
      event: SchoolEvent;
      students: IndividualTournamentTeamPlayer[];
      playersToReplace: { eventId: string; playerId: string }[];
      positions?: Record<string, string>;
    }[]
  ) => void;
  onCloseClick: () => void;
}

interface State {
  relayPositions: RelayPositionValues;
  isEventsForOlderGroup: boolean;
  selectedEvents: SelectedEventItem[];
  isHelpModalOpen: boolean;
  slotAssignmentEvent: SchoolEvent | null;
  slotAssignmentParticipants: any[];
}

export class AddPlayerToEventForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      relayPositions: {},
      isEventsForOlderGroup: false,
      selectedEvents: [],
      isHelpModalOpen: false,
      slotAssignmentEvent: null,
      slotAssignmentParticipants: []
    };
  }

  isLimitEnforced = (event: SchoolEvent) =>
    propz.get<any>(event, ['tournament', 'isEventParticipantLimitEnforced'], true) !== false;

  canOverrideLimit = (event: SchoolEvent) =>
    this.props.user.activeRole === 'ADMIN' &&
    String(this.props.user.activeSchoolId) === String(propz.get(event, ['tournament', 'schoolId'], ''));

  getSchoolPlayersInEvent(event: SchoolEvent): any[] {
    const { user } = this.props;
    const { activeSchoolId, activeSchool } = user;
    const schoolIds: string[] = propz.get(activeSchool, ['schoolIds'], [activeSchoolId]);

    const individualsData = (event.individualsData || []).filter(
      p => schoolIds.includes(p.schoolId) || p.schoolId === activeSchoolId
    );
    if (individualsData.length > 0) return individualsData;

    const allTeamPlayers = (event.teamsData || [])
      .filter(team => schoolIds.includes(team.schoolId) || team.schoolId === activeSchoolId)
      .reduce((acc: any[], team) => [...acc, ...(team.players || [])], []);
    if (allTeamPlayers.length > 0) return allTeamPlayers;

    return (event.players || []).filter(p => schoolIds.includes(p.schoolId) || p.schoolId === activeSchoolId);
  }

  onEventSelect = (event: SchoolEvent) => {
    const { students } = this.props;

    const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
    const hasLimit = this.isLimitEnforced(event) && typeof maxPlayers === 'number' && maxPlayers > 0;

    if (hasLimit) {
      const schoolPlayers = this.getSchoolPlayersInEvent(event);
      const currentCount = schoolPlayers.length;
      const totalAfterAdd = currentCount + students.length;

      if (totalAfterAdd > maxPlayers) {
        if (
          this.canOverrideLimit(event) &&
          window.confirm(
            `Warning: this will exceed the event limit by ${totalAfterAdd - maxPlayers}. Add the participants anyway?`
          )
        ) {
          this.setState(prevState => ({ selectedEvents: [...prevState.selectedEvents, { event }] }));
          return;
        }
        this.setState({
          slotAssignmentEvent: event,
          slotAssignmentParticipants: schoolPlayers
        });
        return;
      }
    }

    this.setState(prevState => ({
      selectedEvents: [...prevState.selectedEvents, { event }]
    }));
  };

  onSlotAssignmentConfirm = (assignments: SlotAssignment[]) => {
    const { slotAssignmentEvent } = this.state;
    if (!slotAssignmentEvent) return;

    const newEventItems: SelectedEventItem[] = assignments.map(assignment => ({
      event: slotAssignmentEvent,
      playerToReplace: assignment.replacingPlayerId
        ? {
            userId: '',
            permissionId: '',
            playerId: assignment.replacingPlayerId,
            firstName: assignment.student.firstName,
            lastName: assignment.student.lastName,
            replacedFirstName: assignment.replacedFirstName || '',
            replacedLastName: assignment.replacedLastName || ''
          }
        : undefined,
      student: assignment.student
    }));

    this.setState(prevState => ({
      selectedEvents: [...prevState.selectedEvents, ...newEventItems],
      slotAssignmentEvent: null,
      slotAssignmentParticipants: []
    }));
  };

  onRemoveEvent = (eventId: string) => {
    this.setState(prevState => ({
      selectedEvents: prevState.selectedEvents.filter(item => item.event.id !== eventId)
    }));
  };

  searchFunction = (text: string) => {
    const { user, students, tournamentId } = this.props;
    const { isEventsForOlderGroup, selectedEvents } = this.state;

    const ageGroupsNaming = propz.get(user, ['activeSchool', 'ageGroupsNaming'], '');
    const ageGroupsCount = AGE_GROUPS[ageGroupsNaming].length;

    const selectedEventsIds = selectedEvents.map(item => item.event.id);

    return searchFunctionHighLevelTournamentEvents(
      user,
      students,
      tournamentId,
      isEventsForOlderGroup,
      selectedEventsIds,
      text
    ).then(events => {
      const studentsAges = students.map(student => propz.get(student, ['form', 'age']));
      const studentsMaxAge = Math.max(...studentsAges);

      if (isEventsForOlderGroup) {
        const olderAges = [studentsMaxAge];
        for (let index = studentsMaxAge + 1; index < ageGroupsCount; index++) {
          olderAges.push(index);
        }
        const filteredEvents = events.filter(event => {
          const { ages } = event;
          return olderAges.some(olderAge => ages.includes(olderAge));
        });
        return sortTournamentEventsByAge(filteredEvents, ageGroupsNaming);
      } else {
        const filteredEvents = events.filter(event => {
          const { ages } = event;
          return ages.includes(studentsMaxAge);
        });
        return sortTournamentEventsByAge(filteredEvents, ageGroupsNaming);
      }
    });
  };

  getEventStudents = (eventId: string) => {
    const students = this.state.selectedEvents.filter(item => item.event.id === eventId)
      .reduce((rows, item) => rows.concat(item.student ? [item.student] : this.props.students), [] as IndividualTournamentTeamPlayer[]);
    return [...new Map(students.map(student => [relayPlayerKey(student), student])).values()];
  };

  hasMissingPositions = () => this.state.selectedEvents.some(item =>
    missingRelayPositions(item.event, this.getEventStudents(item.event.id), this.state.relayPositions[item.event.id]));

  onSubmit = () => {
    if (this.hasMissingPositions()) return;
    const { addPlayerToEvent, students } = this.props;
    const { selectedEvents } = this.state;

    const eventMap = new Map<
      string,
      {
        event: SchoolEvent;
        studentsToAdd: IndividualTournamentTeamPlayer[];
        playersToReplace: { eventId: string; playerId: string }[];
      }
    >();

    selectedEvents.forEach(item => {
      const eventId = item.event.id;
      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, { event: item.event, studentsToAdd: [], playersToReplace: [] });
      }
      const entry = eventMap.get(eventId);
      if (!entry) return;

      const studentToAdd = (item as any).student || null;
      if (studentToAdd) {
        if (!entry.studentsToAdd.find(x => x.userId === studentToAdd.userId)) {
          entry.studentsToAdd.push(studentToAdd);
        }
      } else {
        students.forEach(s => {
          if (!entry.studentsToAdd.find(x => x.userId === s.userId)) {
            entry.studentsToAdd.push(s);
          }
        });
      }

      if (item.playerToReplace) {
        entry.playersToReplace.push({
          eventId,
          playerId: item.playerToReplace.playerId
        });
      }
    });

    const assignments = Array.from(eventMap.values()).map(({ event, studentsToAdd, playersToReplace }) => ({
      event,
      students: studentsToAdd,
      positions: this.state.relayPositions[event.id] || {},
      playersToReplace
    }));

    addPlayerToEvent(assignments);
  };

  renderStudents() {
    return <section className="hltStudentAllocationForm__section" aria-label="Students to allocate">
      <h3>Students <span>{this.props.students.length}</span></h3>
      <div className="hltStudentAllocationForm__students">{this.props.students.map(student => <div className="hltStudentAllocationForm__student" key={relayPlayerKey(student)}>
        <div className="hltStudentAllocationForm__studentCopy"><strong>{getFullName(student)}</strong>
          <small>{student.schoolName || 'School not recorded'}</small><StudentBirthday birthday={student.birthday} labelled />
        </div>
      </div>)}</div>
    </section>;
  }

  renderSelectedEvents() {
    const events = [...new Map(this.state.selectedEvents.map(item => [item.event.id, item.event])).values()];
    return <section className="hltStudentAllocationForm__section" aria-label="Selected events">
      <h3>Selected events <span>{events.length}</span></h3>
      {!events.length ? <div className="hltStudentAllocationForm__empty">No events selected yet. Search above to choose one or more events.</div> :
        <div className="hltStudentAllocationForm__students">{events.map(event => {
          const name = propz.get(event, ['generatedNames', this.props.user.activeSchoolId], '') || propz.get(event, ['generatedNames', 'official'], 'Event');
          const replacements = this.state.selectedEvents.filter(item => item.event.id === event.id && item.playerToReplace);
          const max = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
          return <div className="hltStudentAllocationForm__student" key={event.id}>
            <div className="hltStudentAllocationForm__studentCopy" style={{flex: 1}}><strong>{name}</strong>
              {this.isLimitEnforced(event) && max > 0 && <small>{this.getSchoolPlayersInEvent(event).length}/{max} places currently filled</small>}
              {replacements.map((item, index) => <span className="hltStudentAllocationForm__replacement" key={index}>
                Replaces {item.playerToReplace.replacedLastName} {item.playerToReplace.replacedFirstName}
              </span>)}
              <RelayPositions event={event} eventName={name} students={this.getEventStudents(event.id)} values={this.state.relayPositions[event.id]}
                onChange={(key, value) => this.setState(state => ({relayPositions: {...state.relayPositions,
                  [event.id]: {...state.relayPositions[event.id], [key]: value}}}))} />
            </div>
            <button type="button" className="hltStudentAllocationForm__remove" aria-label={`Remove ${name} from selection`} onClick={() => this.onRemoveEvent(event.id)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
            </button>
          </div>;
        })}</div>}
    </section>;
  }

  renderSlotAssignmentModal() {
    const { user, students } = this.props;
    const { activeSchoolId } = user;
    const { slotAssignmentEvent, slotAssignmentParticipants } = this.state;

    if (!slotAssignmentEvent) return null;

    const maxPlayers = propz.get(slotAssignmentEvent, ['sport', 'defaultLimits', 'maxPlayers']);

    return (
      <SlotAssignmentModal
        isOpen={true}
        event={slotAssignmentEvent}
        newStudents={students}
        currentParticipants={slotAssignmentParticipants}
        maxPlayers={maxPlayers}
        activeSchoolId={activeSchoolId}
        onConfirm={this.onSlotAssignmentConfirm}
        onClose={() => this.setState({ slotAssignmentEvent: null, slotAssignmentParticipants: [] })}
      />
    );
  }

  render() {
    const {user, students, onCloseClick} = this.props;
    const {isEventsForOlderGroup, selectedEvents} = this.state;
    const count = new Set(selectedEvents.map(item => item.event.id)).size;
    return <fieldset className="hltStudentAllocationForm" disabled={this.props.isSaving} aria-busy={this.props.isSaving}>
      <HelpModal isOpen={this.state.isHelpModalOpen} title="Allocate to events" badgeText="Allocation"
        subtitle="Select events and review relay positions before saving." onClose={() => this.setState({isHelpModalOpen: false})}>
        <p>Choose eligible events for your students. If an event is full, choose an existing participant to replace. Relay positions are saved separately for each event.</p>
      </HelpModal>
      {this.renderSlotAssignmentModal()}
      <div className="hltStudentAllocationForm__content">
        <div className="hltStudentAllocationForm__intro"><p>Choose one or more events for the selected students. Set each student's position for relay events.</p>
          <HelpIconButton onClick={() => this.setState({isHelpModalOpen: true})} label="How allocating students to events works" /></div>
        {this.renderStudents()}
        <section className="hltStudentAllocationForm__section">
          <div className="hltStudentAllocationForm__searchHeading"><strong>Search events</strong>
            <Switch value={isEventsForOlderGroup} onChange={() => this.setState({isEventsForOlderGroup: !isEventsForOlderGroup})}
              text="Include events for older groups" name="ageGroup" /></div>
          <Autocomplete disabled={this.props.isSaving} searchFunction={this.searchFunction} clearOnSelect
            getElementTitle={item => propz.get(item, ['generatedNames', user.activeSchoolId], '') || propz.get(item, ['generatedNames', 'official'], '')}
            customClass="mFullWidth" placeholder="Click to see available events" onSelect={this.onEventSelect} />
          <p className="hltStudentAllocationForm__hint">Only eligible events are shown. Already allocated and selected events are excluded.</p>
        </section>
        {this.renderSelectedEvents()}
      </div>
      <footer className="hltStudentAllocationForm__footer"><span>{count} event{count === 1 ? '' : 's'} selected</span><div>
        <button type="button" className="tnBtn tnBtnSecondary" onClick={onCloseClick}>Cancel</button>
        <button type="button" className="tnBtn tnBtnPrimary" disabled={!count || this.hasMissingPositions()} onClick={this.onSubmit}>Save allocations</button>
      </div></footer>
    </fieldset>;
  }
}
